import React, { useEffect, useState } from 'react';
import './userJourneyDrawer.css';
import { getAnalyticsUserDetails } from '../../../api/analytics';

const EVENT_LABELS = {
    page_view: 'Viewed Page',
    search: 'Performed Search',
    trip_view: 'Viewed Trip',
    booking_started: 'Started Booking',
    checkout_started: 'Started Checkout',
    payment_initiated: 'Payment Initiated',
    payment_success: 'Payment Successful',
    payment_failed: 'Payment Failed',
    booking_confirmed: 'Booking Confirmed'
};

// fetchUserDetails defaults to the support-admin platform-wide endpoint.
// The organizer analytics page can pass an org-scoped equivalent.
const UserJourneyDrawer = ({ user, from, to, onClose, fetchUserDetails }) => {
    const fetchUserDetailsFn = fetchUserDetails || getAnalyticsUserDetails;

    const [loading, setLoading] = useState(true);
    const [details, setDetails] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchDetails = async () => {
            if (!user?.id) {
                return;
            }

            try {
                setLoading(true);
                setError('');

                const result = await fetchUserDetailsFn({
                    id: user.id,
                    type: user.type,
                    from,
                    to
                });

                if (result?.data?.status) {
                    setDetails(result?.data?.data);
                } else {
                    setError(result?.message || 'Unable to load user activity.');
                }
            } catch (err) {
                console.error('Failed to fetch user journey:', err);

                setError(err?.response?.data?.message || 'Unable to load user activity.');
            } finally {
                setLoading(false);
            }
        };

        fetchDetails();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, from, to]);

    const userInfo = details?.user || user || {};
    const summary = details?.summary || {};
    const events = details?.events || [];

    const isLoggedIn = userInfo.type === 'user';

    const displayName = isLoggedIn
        ? userInfo.name || `User #${String(userInfo.id || '').slice(-6)}`
        : `Visitor #${String(userInfo.id || '').slice(-6)}`;

    const fullPhoneNumber =
        userInfo.countryCode && userInfo.phoneNumber
            ? `${userInfo.countryCode} ${userInfo.phoneNumber}`
            : userInfo.phoneNumber || null;

    const formatEventDate = (date) => {
        if (!date) {
            return '';
        }

        return new Date(date).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const formatEventTime = (date) => {
        if (!date) {
            return '';
        }

        return new Date(date).toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const getEventClass = (event) => {
        switch (event) {
            case 'payment_success':
            case 'booking_confirmed':
                return 'success';

            case 'payment_failed':
                return 'failed';

            case 'booking_started':
            case 'checkout_started':
            case 'payment_initiated':
                return 'booking';

            case 'search':
                return 'search';

            case 'trip_view':
                return 'trip';

            default:
                return 'default';
        }
    };

    const renderMetadata = (event) => {
        const metadata = event.metadata || {};

        const items = [];

        if (metadata.tripTitle) {
            items.push({
                label: 'Trip',
                value: metadata.tripTitle
            });
        }

        if (metadata.destination) {
            items.push({
                label: 'Destination',
                value: metadata.destination
            });
        }

        if (metadata.place && metadata.place !== metadata.destination) {
            items.push({
                label: 'Place',
                value: metadata.place
            });
        }

        if (metadata.type) {
            items.push({
                label: 'Type',
                value: metadata.type
            });
        }

        if (metadata.destType && metadata.destType !== metadata.type) {
            items.push({
                label: 'Trip Type',
                value: metadata.destType
            });
        }

        if (metadata.travellerCount) {
            items.push({
                label: 'Travellers',
                value: metadata.travellerCount
            });
        }

        if (metadata.amount) {
            items.push({
                label: 'Amount',
                value: metadata.amount
            });
        }

        if (metadata.orderId) {
            items.push({
                label: 'Order',
                value: metadata.orderId
            });
        }

        if (metadata.paymentId) {
            items.push({
                label: 'Payment',
                value: metadata.paymentId
            });
        }

        if (metadata.status) {
            items.push({
                label: 'Status',
                value: metadata.status
            });
        }

        if (!items.length) {
            return null;
        }

        return (
            <div className='userJourneyMetadata'>
                {items.map((item, index) => (
                    <div className='userJourneyMetadataItem' key={`${item.label}-${index}`}>
                        <span>{item.label}</span>
                        <strong>{item.value}</strong>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className='userJourneyOverlay' onClick={onClose}>
            <div className='userJourneyDrawer' onClick={(event) => event.stopPropagation()}>
                {/* ----------------------------------------- */}
                {/* Header */}
                {/* ----------------------------------------- */}

                <div className='userJourneyHeader'>
                    <div className='userJourneyHeaderMain'>
                        <div className='userJourneyIdentity'>
                            <div className='userJourneyAvatar'>{displayName?.charAt(0)?.toUpperCase() || 'U'}</div>

                            <div className='userJourneyIdentityContent'>
                                <div className='userJourneyNameRow'>
                                    <h2>{displayName}</h2>

                                    <span className={`userJourneyType ${isLoggedIn ? 'loggedIn' : 'anonymous'}`}>
                                        {isLoggedIn ? 'Logged in' : 'Anonymous'}
                                    </span>
                                </div>

                                {/* ----------------------------------------- */}
                                {/* Contact Information */}
                                {/* ----------------------------------------- */}

                                {isLoggedIn && (
                                    <div className='userJourneyContact'>
                                        {fullPhoneNumber && (
                                            <div className='userJourneyContactItem'>
                                                <span className='contactIcon'>📱</span>

                                                <span>{fullPhoneNumber}</span>
                                            </div>
                                        )}

                                        {userInfo.emailId && (
                                            <div className='userJourneyContactItem'>
                                                <span className='contactIcon'>✉</span>

                                                <span>{userInfo.emailId}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <button type='button' className='userJourneyClose' onClick={onClose}>
                        ×
                    </button>
                </div>

                {/* ----------------------------------------- */}
                {/* Loading */}
                {/* ----------------------------------------- */}

                {loading ? (
                    <div className='userJourneyLoading'>
                        <div className='userJourneyLoader' />
                        <span>Loading user activity...</span>
                    </div>
                ) : error ? (
                    <div className='userJourneyError'>
                        <div className='userJourneyErrorIcon'>!</div>

                        <p>{error}</p>
                    </div>
                ) : (
                    <>
                        {/* ----------------------------------------- */}
                        {/* Summary */}
                        {/* ----------------------------------------- */}

                        <div className='userJourneySummary'>
                            <div className='userJourneySummaryCard'>
                                <span>Sessions</span>
                                <strong>{summary.sessions || 0}</strong>
                            </div>

                            <div className='userJourneySummaryCard'>
                                <span>Searches</span>
                                <strong>{summary.searches || 0}</strong>
                            </div>

                            <div className='userJourneySummaryCard'>
                                <span>Trip Views</span>
                                <strong>{summary.tripViews || 0}</strong>
                            </div>

                            <div className='userJourneySummaryCard paid'>
                                <span>Paid</span>
                                <strong>{summary.paymentSuccess || 0}</strong>
                            </div>
                        </div>

                        {/* ----------------------------------------- */}
                        {/* Journey */}
                        {/* ----------------------------------------- */}

                        <div className='userJourneyContent'>
                            <div className='userJourneyTimelineHeader'>
                                <div>
                                    <h3>User Journey</h3>
                                    <p>Latest activity appears first</p>
                                </div>

                                <span className='userJourneyEventCount'>
                                    {events.length} {events.length === 1 ? 'event' : 'events'}
                                </span>
                            </div>

                            {events.length === 0 ? (
                                <div className='userJourneyEmpty'>
                                    <div className='userJourneyEmptyIcon'>◌</div>

                                    <h4>No activity found</h4>

                                    <p>This user has no tracked activity for the selected period.</p>
                                </div>
                            ) : (
                                <div className='userJourneyTimeline'>
                                    {events.map((event, index) => {
                                        const eventClass = getEventClass(event.event);

                                        return (
                                            <div
                                                className='userJourneyTimelineItem'
                                                key={event.id || `${event.event}-${index}`}
                                            >
                                                {/* Timeline line */}

                                                {index !== events.length - 1 && <div className='timelineLine' />}

                                                {/* Timeline dot */}

                                                <div className={`timelineDot ${eventClass}`}>
                                                    <span />
                                                </div>

                                                {/* Event content */}

                                                <div className='userJourneyEventCard'>
                                                    <div className='userJourneyEventHeader'>
                                                        <div className='userJourneyEventMain'>
                                                            <h4>{EVENT_LABELS[event.event] || event.event}</h4>

                                                            {event.page && (
                                                                <span className='userJourneyPage'>{event.page}</span>
                                                            )}
                                                        </div>

                                                        <div className='userJourneyEventTime'>
                                                            <strong>{formatEventTime(event.createdAt)}</strong>

                                                            <span>{formatEventDate(event.createdAt)}</span>
                                                        </div>
                                                    </div>

                                                    {renderMetadata(event)}

                                                    <div className='userJourneyEventFooter'>
                                                        {event.platform && (
                                                            <span className='userJourneyPlatform'>
                                                                {event.platform}
                                                            </span>
                                                        )}

                                                        {event.sessionId && (
                                                            <span className='userJourneySession'>
                                                                Session {String(event.sessionId).slice(-8)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default UserJourneyDrawer;
